/**
 * Hero with Side Banners — Splide + control de video por diapositiva.
 * - Opciones desde data-* (autoplay, interval, pause hover/focus, arrows/dots).
 * - Diapositivas con <video data-hero-video>: el JS reproduce (muted, playsinline)
 *   la del slide activo, pausa las demas, avanza al terminar (ended) si el
 *   carrusel tiene autoplay, y cambia a la fuente movil cuando toca.
 *   data-splide-interval = 60s en esas slides (tope; normalmente manda el ended).
 * - El video se reproduce siempre (decision de negocio 2026-09-07): NO se
 *   respeta prefers-reduced-motion aqui, es un fondo mudo y corto.
 * - matchMedia 768px: re-montaje al cambiar de breakpoint (re-elige la fuente del video).
 */
(function () {
  'use strict';

  var BP = '(max-width: 768px)';
  var FALLBACK_INTERVAL = 4500;
  var BIG_INTERVAL = '60000';

  var mqMobile = window.matchMedia(BP);
  var instances = new WeakMap();

  function getBool(v) {
    return String(v) === 'true';
  }

  function getOptions(el, isMobile) {
    return {
      type: 'loop',
      autoplay: getBool(el.dataset.autoplay),
      interval: Number(el.dataset.interval) || FALLBACK_INTERVAL,
      pauseOnHover: getBool(el.dataset.pauseHover),
      pauseOnFocus: getBool(el.dataset.pauseFocus),
      arrows: isMobile ? getBool(el.dataset.arrowsMobile) : getBool(el.dataset.arrowsDesktop),
      pagination: isMobile ? getBool(el.dataset.dotsMobile) : getBool(el.dataset.dotsDesktop),
      keyboard: 'global',
      updateOnMove: true,
      classes: {
        arrows: 'splide__arrows',
        arrow: 'splide__arrow',
        prev: 'splide__arrow--prev',
        next: 'splide__arrow--next'
      }
    };
  }

  function forcePlay(state) {
    if (!getBool(state.el.dataset.autoplay)) return;
    try {
      var ap = state.splide.Components.Autoplay;
      if (ap) ap.play();
    } catch (_) {}
  }

  /* ---------- video ---------- */

  function pickSrc(v) {
    return mqMobile.matches
      ? v.dataset.srcMobile || v.dataset.srcDesktop
      : v.dataset.srcDesktop || v.dataset.srcMobile;
  }

  function clearFallback(state) {
    if (state.fallback) {
      clearTimeout(state.fallback);
      state.fallback = null;
    }
  }

  function advanceLater(state) {
    if (!getBool(state.el.dataset.autoplay)) return;
    clearFallback(state);
    state.fallback = setTimeout(function () {
      try { state.splide.go('>'); } catch (_) {}
    }, Number(state.el.dataset.interval) || FALLBACK_INTERVAL);
  }

  function armGestureRetry(state) {
    if (state.gestureArmed) return;
    state.gestureArmed = true;
    // El autoplay puede estar bloqueado (p. ej. dentro del iframe del editor
    // de temas). Reintenta reproducir en la primera interaccion del usuario.
    var retry = function () {
      document.removeEventListener('pointerdown', retry, true);
      document.removeEventListener('keydown', retry, true);
      document.removeEventListener('touchstart', retry, true);
      state.gestureArmed = false;
      var v = activeVideo(state);
      if (v) { try { v.play(); } catch (_) {} }
    };
    document.addEventListener('pointerdown', retry, true);
    document.addEventListener('keydown', retry, true);
    document.addEventListener('touchstart', retry, true);
    // Y que el carrusel siga girando mientras tanto.
    state.el.querySelectorAll('[data-hero-video]').forEach(function (vid) {
      var li = vid.closest('.splide__slide');
      if (li) li.removeAttribute('data-splide-interval');
    });
    forcePlay(state);
  }

  function kick(v, state) {
    var p;
    try { p = v.play(); } catch (_) { p = null; }
    if (p && typeof p.catch === 'function') {
      p.catch(function (err) {
        if (err && err.name === 'NotAllowedError') armGestureRetry(state);
      });
    }
  }

  function kickWhenReady(v, state) {
    if (v.readyState >= 2) {
      kick(v, state);
      return;
    }
    var once = function () {
      v.removeEventListener('loadeddata', once);
      v.removeEventListener('canplay', once);
      kick(v, state);
    };
    v.addEventListener('loadeddata', once);
    v.addEventListener('canplay', once);
    kick(v, state);
  }

  function playVideo(v, state) {
    v.muted = true;
    v.loop = !getBool(state.el.dataset.autoplay);

    var useMobile =
      mqMobile.matches &&
      v.dataset.srcMobile &&
      v.dataset.srcMobile !== v.dataset.srcDesktop;

    if (useMobile && v.dataset.appliedSrc !== 'mobile') {
      v.dataset.appliedSrc = 'mobile';
      v.setAttribute('src', v.dataset.srcMobile);
      v.load();
    } else if (!useMobile && v.dataset.appliedSrc === 'mobile') {
      v.dataset.appliedSrc = 'desktop';
      v.removeAttribute('src');
      v.load();
    }
    // si no, se deja el <source> nativo (URL de escritorio) intacto

    kickWhenReady(v, state);
  }

  function stopVideo(v) {
    v.classList.remove('is-playing');
    try { v.pause(); } catch (_) {}
    try { v.currentTime = 0; } catch (_) {}
  }

  function realSlides(state) {
    try {
      return state.splide.Components.Elements.slides || [];
    } catch (_) {
      return [];
    }
  }

  function activeVideo(state) {
    var li = realSlides(state)[state.splide.index];
    if (!li) return null;
    for (var i = 0; i < state.videos.length; i++) {
      if (li.contains(state.videos[i])) return state.videos[i];
    }
    return null;
  }

  function syncVideos(state) {
    clearFallback(state);
    var curLi = realSlides(state)[state.splide.index];
    if (!curLi) return;
    state.videos.forEach(function (v) {
      if (v.closest('.splide__slide') === curLi) playVideo(v, state);
      else stopVideo(v);
    });
  }

  // Splide (type:loop) clona las slides -> tambien clona los <video> con su
  // <source>. Que los clones no descarguen nada ni intenten reproducir.
  function neutralizeClones(state) {
    state.el.querySelectorAll('[data-hero-video]').forEach(function (v) {
      if (state.videos.indexOf(v) > -1) return;
      v.preload = 'none';
      v.removeAttribute('autoplay');
      v.muted = true;
      try { v.pause(); } catch (_) {}
    });
  }

  // El enlace de una slide oculta (aria-hidden de Splide) no debe ser
  // enfocable: si retiene el foco, el navegador avisa. inert lo saca.
  function syncLinks(state) {
    var curLi = realSlides(state)[state.splide.index];
    state.el.querySelectorAll('.hero-slide-link').forEach(function (a) {
      if (a.closest('.splide__slide') === curLi) {
        a.removeAttribute('inert');
        a.removeAttribute('tabindex');
      } else {
        a.setAttribute('inert', '');
        a.setAttribute('tabindex', '-1');
      }
    });
  }

  function onEnded(state, v) {
    if (!getBool(state.el.dataset.autoplay)) return;
    if (realSlides(state)[state.splide.index] !== v.closest('.splide__slide')) return;
    try {
      var ap = state.splide.Components.Autoplay;
      if (ap && typeof ap.isPaused === 'function' && ap.isPaused()) return;
    } catch (_) {}
    try { state.splide.go('>'); } catch (_) {}
  }

  function wireVideos(state) {
    state.videos.forEach(function (v) {
      if (v.dataset.wired) return;
      v.dataset.wired = '1';
      v.muted = true;
      v.setAttribute('muted', '');
      if (!v.paused) v.classList.add('is-playing');
      v.addEventListener('playing', function () {
        v.classList.add('is-playing');
      });
      v.addEventListener('ended', function () {
        onEnded(state, v);
      });
      v.addEventListener('error', function () {
        v.classList.remove('is-playing');
        advanceLater(state);
      });
    });
  }

  function markVideoSlides(el) {
    el.querySelectorAll('[data-hero-video]').forEach(function (v) {
      var li = v.closest('.splide__slide');
      if (li) li.setAttribute('data-splide-interval', BIG_INTERVAL);
    });
  }

  function mount(state) {
    markVideoSlides(state.el);
    var splide = new Splide(state.el, getOptions(state.el, mqMobile.matches));
    state.splide = splide;
    splide.on('mounted', function () {
      forcePlay(state);
      syncVideos(state);
      syncLinks(state);
    });
    splide.on('moved', function () {
      syncVideos(state);
      syncLinks(state);
    });
    splide.on('active', function (Slide) {
      if (Slide && Slide.isClone) return;
      var v = state.videos.filter(function (x) { return Slide.slide.contains(x); })[0];
      if (v) playVideo(v, state);
    });
    splide.on('inactive', function (Slide) {
      var v = state.videos.filter(function (x) { return Slide.slide.contains(x); })[0];
      if (v) stopVideo(v);
    });
    splide.on('autoplay:pause', function () {
      var v = activeVideo(state);
      if (v) {
        try { v.pause(); } catch (_) {}
      }
    });
    splide.on('autoplay:play', function () {
      var v = activeVideo(state);
      if (v) playVideo(v, state);
    });
    splide.mount();
    neutralizeClones(state);
  }

  function initOne(el) {
    if (instances.has(el)) return;

    var state = {
      el: el,
      splide: null,
      videos: Array.prototype.slice.call(el.querySelectorAll('[data-hero-video]')),
      fallback: null,
      isMobile: mqMobile.matches
    };

    wireVideos(state);
    mount(state);

    var onChange = function (e) {
      if (e.matches === state.isMobile) return;
      state.isMobile = e.matches;
      clearFallback(state);
      try { state.splide.destroy(); } catch (_) {}
      mount(state);
    };

    var onVis = function () {
      var v = activeVideo(state);
      if (document.hidden) {
        if (v) {
          try { v.pause(); } catch (_) {}
        }
      } else {
        forcePlay(state);
        if (v) playVideo(v, state);
      }
    };

    if (mqMobile.addEventListener) mqMobile.addEventListener('change', onChange);
    else mqMobile.addListener(onChange);
    document.addEventListener('visibilitychange', onVis);

    instances.set(el, { state: state, onChange: onChange, onVis: onVis });
  }

  function destroyOne(el) {
    var ref = instances.get(el);
    if (!ref) return;
    try { ref.state.splide.destroy(); } catch (_) {}
    clearFallback(ref.state);
    if (mqMobile.removeEventListener) mqMobile.removeEventListener('change', ref.onChange);
    else mqMobile.removeListener(ref.onChange);
    document.removeEventListener('visibilitychange', ref.onVis);
    instances.delete(el);
  }

  function initAll(root) {
    (root || document).querySelectorAll('.hero-slider.splide').forEach(initOne);
  }

  function destroyAll(root) {
    (root || document).querySelectorAll('.hero-slider.splide').forEach(destroyOne);
  }

  function ready(fn) {
    if (typeof Splide !== 'undefined') return fn();
    window.addEventListener('load', function () {
      if (typeof Splide !== 'undefined') fn();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    ready(function () { initAll(); });
  });
  document.addEventListener('shopify:section:load', function (e) {
    ready(function () { initAll(e.target); });
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroyAll(e.target);
  });
})();
